import React, { useState } from "react";
import "./DailyCollection.css";
import {
  FaFileInvoiceDollar,
  FaWallet,
  FaRedo,
  FaReceipt,
  FaCalendarAlt,
  FaUser,
  FaHashtag,
  FaCheckCircle,
  FaUserPlus,
  FaCreditCard,
  FaMobileAlt,
  FaMoneyBillAlt,
  FaExternalLinkAlt
} from "react-icons/fa";
import { Pagination } from "@mui/material";

const DailyCollection = ({ reportData }) => {
  const {
    totalPayments = 0,
    totalCollected = 0,
    refundAmount = 0,
    cash = 0,
    card = 0,
    upi = 0,
    transactions = []
  } = reportData || {};

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const totalPages = Math.ceil(transactions.length / itemsPerPage) || 1;
  const paginatedTransactions = transactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calculate payment distribution percentages
  const cashPercent = totalCollected ? (cash / totalCollected) * 100 : 0;
  const cardPercent = totalCollected ? (card / totalCollected) * 100 : 0;
  const upiPercent = totalCollected ? (upi / totalCollected) * 100 : 0;

  const renderPaymentModeIcon = (mode) => {
    const m = (mode || "").toLowerCase();
    if (m.includes("card")) {
      return <FaCreditCard size={13} color="#0284c7" title="Card" />;
    }
    if (m.includes("upi")) {
      return <FaMobileAlt size={13} color="#16a34a" title="UPI" />;
    }
    return <FaMoneyBillAlt size={13} color="#d97706" title="Cash" />;
  };

  const handleRowClick = (tx) => {
    const targetId = tx.appointmentId || tx.invoiceNumber;
    if (!targetId || targetId === "N/A") return;
    window.open(`/outpatient/appointments/patientInfoPanel/${targetId}`, "_blank");
  };

  return (
    <div className="daily-collection-container">
      {/* KPI Stat Cards Grid */}
      <div className="collection-kpi-grid">
        {/* Card 1: Total Payments */}
        <div className="collection-kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-title">TOTAL PAYMENTS</span>
            <div className="kpi-icon-badge blue-badge">
              <FaFileInvoiceDollar size={14} color="#2563eb" />
            </div>
          </div>
          <div className="kpi-card-body">
            <h3 className="kpi-card-value">{totalPayments}</h3>
            <span className="kpi-subtext">Completed & Processed</span>
          </div>
        </div>

        {/* Card 2: Total Amount Collected */}
        <div className="collection-kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-title">TOTAL AMOUNT COLLECTED</span>
            <div className="kpi-icon-badge green-badge">
              <FaWallet size={14} color="#16a34a" />
            </div>
          </div>
          <div className="kpi-card-body">
            <h3 className="kpi-card-value">
              ₹ {totalCollected.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h3>

            {/* Mini Distribution Track */}
            <div className="payment-bar-container" title="Payment Distribution">
              <div
                className="payment-bar cash-bar"
                style={{ width: `${cashPercent}%` }}
                title={`Cash: ₹${cash}`}
              ></div>
              <div
                className="payment-bar card-bar"
                style={{ width: `${cardPercent}%` }}
                title={`Card: ₹${card}`}
              ></div>
              <div
                className="payment-bar upi-bar"
                style={{ width: `${upiPercent}%` }}
                title={`UPI: ₹${upi}`}
              ></div>
            </div>

            {/* Mode Pills */}
            <div className="payment-breakdown-pills">
              <span className="mode-pill">
                <FaMoneyBillAlt size={11} color="#d97706" /> Cash: <strong>₹ {cash.toLocaleString("en-IN")}</strong>
              </span>
              <span className="mode-pill">
                <FaCreditCard size={11} color="#0284c7" /> Card: <strong>₹ {card.toLocaleString("en-IN")}</strong>
              </span>
              <span className="mode-pill">
                <FaMobileAlt size={11} color="#16a34a" /> UPI: <strong>₹ {upi.toLocaleString("en-IN")}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Card 3: Refund Amount */}
        <div className="collection-kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-title">REFUND AMOUNT</span>
            <div className="kpi-icon-badge red-badge">
              <FaRedo size={14} color="#e11d48" />
            </div>
          </div>
          <div className="kpi-card-body">
            <h3 className="kpi-card-value">
              ₹ {refundAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h3>
            <span className="kpi-subtext red-subtext">Refunds & Reversals</span>
          </div>
        </div>
      </div>

      {/* 🔹 Detailed Transactions Section */}
      <div className="transactions-section">
        <div className="transactions-section-header">
          <div className="header-title-group">
            <FaReceipt size={16} color="#2563eb" />
            <h3 className="table-title">Detailed Transactions</h3>
          </div>
          <span className="count-pill-badge">{transactions.length} Transactions</span>
        </div>

        {transactions.length > 0 ? (
          <>
            <div className="table-responsive">
              <table className="report-data-table">
                <thead>
                  <tr>
                    <th>
                      <div className="th-flex">
                        <FaCalendarAlt size={11} color="#64748b" />
                        <span>DATE</span>
                      </div>
                    </th>
                    <th>
                      <div className="th-flex">
                        <FaUser size={11} color="#64748b" />
                        <span>PATIENT</span>
                      </div>
                    </th>
                    <th>
                      <div className="th-flex">
                        <FaHashtag size={11} color="#64748b" />
                        <span>INVOICE NO.</span>
                      </div>
                    </th>
                    <th className="text-right">
                      <div className="th-flex right-justify">
                        <FaCheckCircle size={11} color="#64748b" />
                        <span>PAID AMOUNT</span>
                      </div>
                    </th>
                    <th>
                      <div className="th-flex">
                        <FaUserPlus size={11} color="#64748b" />
                        <span>ADDED BY</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map((tx, index) => {
                    const isRefund = tx.transactionType === "REFUND" || (tx.paymentMode || "").toLowerCase().includes("refund");
                    const formattedAmt = (tx.paidAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
                    return (
                      <tr
                        key={index}
                        className={`clickable-row ${isRefund ? "refund-row" : ""}`}
                        onClick={() => handleRowClick(tx)}
                        title="Click to view Patient Info Panel"
                      >
                        <td>
                          <span className="date-pill-tag">{tx.date}</span>
                        </td>
                        <td>
                          <strong className="patient-name">{tx.patient}</strong>
                        </td>
                        <td>
                          <span className="invoice-no-tag">
                            {tx.invoiceNumber || "N/A"}
                            <FaExternalLinkAlt size={10} style={{ marginLeft: 6, opacity: 0.7 }} />
                          </span>
                        </td>
                        <td className="text-right">
                          <div className="paid-amount-cell">
                            <span className="pay-mode-icon-box">
                              {renderPaymentModeIcon(tx.paymentMode)}
                            </span>
                            <span className={`amount-val ${isRefund ? "refund-val" : "paid-val"}`}>
                              {isRefund ? `- ₹ ${formattedAmt}` : `₹ ${formattedAmt}`}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="staff-pill">{tx.addedBy || "Admin"}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className="table-pagination-footer">
              <span className="pagination-info-text">
                Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> to{" "}
                <strong>{Math.min(currentPage * itemsPerPage, transactions.length)}</strong> of{" "}
                <strong>{transactions.length}</strong> transactions
              </span>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, value) => setCurrentPage(value)}
                color="primary"
                variant="outlined"
                shape="rounded"
                size="small"
              />
            </div>
          </>
        ) : (
          <div className="no-data">No transactions recorded for the selected range</div>
        )}
      </div>
    </div>
  );
};

export default DailyCollection;
